import { useState } from "react";
import { actionProps } from "@/lib/controlAnalytics";
import { Link } from "react-router-dom";
import CompanyLink from "@/components/common/CompanyLink";
import CompanyLogo from "@/components/common/CompanyLogo";
import TailoredResumeDialog from "@/components/envoy/history/TailoredResumeDialog";
import { buttonClass } from "@/components/common/controls";
import { ArrowUpRightIcon, CheckIcon, CloseIcon, ExpandIcon } from "@/components/icons";
import PostingOpening from "@/components/posting/PostingOpening";
import RecordList from "@/components/posting/RecordList";
import { FitBar, KeywordChips, StatusLabel } from "@/components/posting/parts";
import type { SearchPosting } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import type { PostingFit } from "@/lib/api/resumeService";
import { SOURCE_NAME, tailorStage } from "@/lib/applications";
import { usePostingEvents } from "@/lib/hooks/catalogue";
import { useAdding, useApplicationEvents } from "@/lib/hooks/envoy";
import { placeAndSetting } from "@/lib/openings";
import {
    applicationChip,
    canMarkSent,
    canSkip,
    dayOf,
    fitNow,
    NO_KEYWORDS_LINE,
    offersSkip,
    postedDay,
    postingRecord,
    tailorFailure,
    triedAgain,
} from "@/lib/posting";
import { postingDetailPath, ROUTES } from "@/lib/routes";
import { webUrl } from "@/lib/safeUrl";
import { useGate } from "@/components/site/gateContext";
import { SaveButton, TailorButton } from "@/components/site/PostingActions";

/** A line in the Fit panel when there is nothing to score. */
const EMPTY = "m-0 text-[14px] leading-5 text-fg-muted [text-wrap:pretty]";

/** A section's heading. */
const HEADING = "m-0 text-[18px] leading-6 font-semibold";

/** The label over a group of keywords, in words as well as colour. */
const GROUP_LABEL = "text-[12px] leading-4 font-medium text-fg-muted";

/**
 * One posting beside the list: what it is, where the person's
 * application stands, the fit of their resume as it stands, the start
 * of the posting's text and the record. The full page has the rest.
 * For a visitor, the same posting with Apply on their site and Save;
 * the fit says what a resume would show, with Add my resume. A member
 * with no resume saved gets the same, to the upload on their resume page.
 *
 * One button wears the gradient, the next step: Tailor my resume until
 * there is a resume to send, then Resume ready · Apply on their site in its
 * place, with the top button saying View employer site so there is one
 * Apply. A tailor that failed says so and why, over Try again.
 *
 * A desktop's only: on mobile it is never rendered (`drawerBeside`), and a
 * posting opens on its own page.
 */
export default function JobDrawer({
    posting,
    application,
    fit,
    onClose,
    onSent,
    onSkip,
    member = true,
}: {
    /** Signed in: the application, the fit and its actions. Signed out, the posting and Save. */
    member?: boolean;
    posting: SearchPosting;
    application: Application | undefined;
    fit: PostingFit | null | undefined;
    onClose: () => void;
    onSent: (application: Application) => void;
    onSkip: () => void;
}) {
    const postingEvents = usePostingEvents(posting.posting_id);
    const events = useApplicationEvents(application?.application_id ?? null);
    const chip = applicationChip(application, posting);
    const view = fit ? fitNow(fit) : null;
    const record = postingRecord(postingEvents.data ?? [], events.data ?? []);
    const { ask } = useGate();
    const stage = tailorStage(application);
    // Only a web address is an address; a hostile apply address falls back to the posting's.
    const postingAt = webUrl(posting.url);
    const applyAt = webUrl(posting.apply_url) ?? postingAt;
    const where = placeAndSetting(posting);
    const board = SOURCE_NAME[posting.source] ?? "their site";
    const scored = member && view && view.asked > 0 ? view : null;
    const canTailor = member && (canSkip(application) || stage === "tailored");
    const tailorLeads = canTailor && stage !== "tailored";
    // A tailor that failed with no resume says so, and why once the record is read.
    const failed = application?.status === "FAILED" && stage !== "tailored";
    // Tried again, the reason stays, faded, until the next attempt ends.
    const asked = useAdding(posting.posting_id);
    const retrying = member && triedAgain({ failed, stage, events: events.data ?? [], asked });
    const why = failed || retrying ? tailorFailure(events.data ?? []) : null;
    // A written resume to send: the fit block's button is the apply step.
    const resumeId = canTailor && stage === "tailored" ? application?.resume_id : null;
    const [viewing, setViewing] = useState(false);

    // The drawer stays in view under the header as the list scrolls, and
    // its body scrolls on its own.
    return (
        <aside
            aria-label="Posting"
            className="flex min-w-0 max-w-[480px] flex-[1_1_400px] flex-col rounded-card border border-line bg-card shadow-card sticky top-[84px] max-h-[calc(100dvh-104px)]"
        >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line-soft py-2.5 pl-6 pr-[18px]">
                <Link
                    {...actionProps("posting_full_page_clicked", {
                        source: "JobDrawer",
                        posting_id: posting.posting_id,
                    })}
                    to={postingDetailPath(posting.posting_id)}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-fg-muted hover:text-brand-ink"
                >
                    <ExpandIcon className="size-[13px]" />
                    Open full page
                </Link>
                <button
                    {...actionProps("posting_drawer_closed", {
                        source: "JobDrawer",
                        posting_id: posting.posting_id,
                    })}
                    type="button"
                    onClick={onClose}
                    aria-label="Close posting"
                    className="grid size-7 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-hover"
                >
                    <CloseIcon className="size-[15px]" />
                </button>
            </div>
            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-5">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col">
                        <h2 className="m-0 font-display text-[28px] leading-[34px] font-bold tracking-[-0.02em] [text-wrap:balance]">
                            {posting.title}
                        </h2>
                        <span className="mt-3 inline-flex items-center gap-2.5 text-[16px] leading-6 font-semibold">
                            <CompanyLogo url={posting.logo_url ?? null} company={posting.company} />
                            <CompanyLink
                                company={posting.company}
                                logoUrl={posting.logo_url ?? null}
                                source={posting.source}
                                board={posting.board}
                                from="JobDrawer"
                            />
                        </span>
                        {where && <span className="mt-2 text-[14px] leading-5">{where}</span>}
                        <span className="mt-0.5 text-[14px] leading-5 text-fg-faint">
                            {posting.posted_at && `Posted ${postedDay(posting.posted_at)} · `}
                            Found {dayOf(posting.first_seen_at)} · via{" "}
                            {postingAt === applyAt ? (
                                board
                            ) : (
                                <a
                                    {...actionProps("posting_source_open_clicked", {
                                        source: "JobDrawer",
                                        posting_id: posting.posting_id,
                                        option: posting.source,
                                    })}
                                    href={postingAt}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Open on ${board}`}
                                    className="font-medium text-brand-ink"
                                >
                                    {board}
                                </a>
                            )}
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3">
                            <a
                                {...actionProps("posting_source_open_clicked", {
                                    source: "JobDrawer",
                                    posting_id: posting.posting_id,
                                    option: "apply",
                                })}
                                href={applyAt}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={buttonClass(
                                    tailorLeads || resumeId ? "outline" : "primary",
                                    "tall",
                                    "grow",
                                )}
                            >
                                {resumeId ? "View employer site" : "Apply on their site"}
                                <ArrowUpRightIcon className="size-[15px]" />
                            </a>
                            <SaveButton
                                postingId={posting.posting_id}
                                title={posting.title}
                                size="tall"
                            />
                        </div>
                        {member && (
                            <div className="flex min-h-6 items-center justify-between gap-3">
                                <StatusLabel label={chip.label} tone={chip.tone} />
                                <span className="flex items-center gap-3">
                                    {application && canMarkSent(application) && (
                                        <button
                                            {...actionProps("mark_sent_opened", {
                                                source: "JobDrawer",
                                                application_id: application.application_id,
                                                posting_id: posting.posting_id,
                                            })}
                                            type="button"
                                            onClick={() => onSent(application)}
                                            className={buttonClass("outline", "tall")}
                                        >
                                            <CheckIcon className="size-3.5" strokeWidth={2.4} />
                                            Mark applied
                                        </button>
                                    )}
                                    {offersSkip(application) && (
                                        <button
                                            {...actionProps("skip_opened", {
                                                source: "JobDrawer",
                                                posting_id: posting.posting_id,
                                            })}
                                            type="button"
                                            onClick={onSkip}
                                            className="rounded-md px-1 text-[15px] leading-[22px] font-medium text-fg-muted transition-colors hover:text-fg"
                                        >
                                            Skip
                                        </button>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <section className="-mx-3.5 flex flex-col gap-3 rounded-[14px] bg-card-raised px-3.5 py-4">
                    {scored ? (
                        <>
                            <div className="flex flex-col gap-0.5">
                                <h3 className={HEADING}>
                                    <span className="text-brand-ink">
                                        {scored.covered} of {scored.asked}
                                    </span>{" "}
                                    keywords matched
                                </h3>
                                <span className="text-[12px] leading-4 text-fg-muted">
                                    Based on your current resume
                                </span>
                            </div>
                            <FitBar share={scored.share} height={8} />
                        </>
                    ) : (
                        <>
                            <h3 className={HEADING}>Fit</h3>
                            {!member ? (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className={EMPTY}>
                                        Add your resume to see which of its keywords you already
                                        have.
                                    </p>
                                    <button
                                        {...actionProps("resume_upload_opened", {
                                            source: "JobDrawer",
                                            posting_id: posting.posting_id,
                                        })}
                                        type="button"
                                        onClick={() => ask("upload")}
                                        className={buttonClass("secondary", "md")}
                                    >
                                        Add my resume
                                    </button>
                                </div>
                            ) : fit === undefined ? (
                                <p className={EMPTY}>Scoring the fit…</p>
                            ) : !view ? (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className={EMPTY}>No resume saved to score it against.</p>
                                    <Link
                                        {...actionProps("resume_upload_opened", {
                                            source: "JobDrawer",
                                            posting_id: posting.posting_id,
                                        })}
                                        to={`${ROUTES.RESUME}?upload=1`}
                                        className={buttonClass("secondary", "md")}
                                    >
                                        Add my resume
                                    </Link>
                                </div>
                            ) : (
                                <p className={EMPTY}>{NO_KEYWORDS_LINE}</p>
                            )}
                        </>
                    )}
                    {member && (failed || retrying) && (
                        <p
                            role="status"
                            aria-busy={retrying}
                            className={`m-0 text-[14px] leading-5 transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] [text-wrap:pretty] ${retrying ? "opacity-45" : ""}`}
                        >
                            <span className="font-semibold text-negative">Tailoring failed</span>
                            {why ? `: ${why}. ` : ". "}
                            No resume was written.
                        </p>
                    )}
                    {resumeId ? (
                        <>
                            <a
                                {...actionProps("posting_source_open_clicked", {
                                    source: "JobDrawer",
                                    posting_id: posting.posting_id,
                                    option: "apply_ready",
                                })}
                                href={applyAt}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={buttonClass("primary", "tall", "w-full gap-2")}
                            >
                                <CheckIcon className="size-4" strokeWidth={2.4} />
                                Resume ready · Apply on their site
                                <ArrowUpRightIcon className="size-[15px]" />
                            </a>
                            <button
                                {...actionProps("tailored_pdf_open_clicked", {
                                    source: "JobDrawer",
                                    resume_id: resumeId,
                                    posting_id: posting.posting_id,
                                })}
                                type="button"
                                onClick={() => setViewing(true)}
                                className="self-center rounded-md px-1 text-[14px] leading-5 font-medium text-fg-muted transition-colors hover:text-fg"
                            >
                                See your tailored resume
                            </button>
                            <TailoredResumeDialog
                                open={viewing}
                                row={{ resumeId }}
                                onClose={() => setViewing(false)}
                            />
                        </>
                    ) : (
                        canTailor && (
                            <TailorButton
                                postingId={posting.posting_id}
                                job={posting}
                                failed={failed}
                                retrying={retrying}
                                queued={stage === "tailoring"}
                                tailored={stage === "tailored"}
                                resumeId={application?.resume_id}
                                size="tall"
                                outline={!tailorLeads}
                            />
                        )
                    )}
                    {scored && scored.coveredTerms.length > 0 && (
                        <div className="mt-1 flex flex-col gap-2">
                            <span className={GROUP_LABEL}>Matched keywords</span>
                            <KeywordChips terms={scored.coveredTerms} kind="covered" quiet />
                        </div>
                    )}
                    {scored && scored.missing.length > 0 && (
                        <div className="mt-1 flex flex-col gap-2">
                            <span className={GROUP_LABEL}>Missing keywords</span>
                            <KeywordChips terms={scored.missing} kind="missing" quiet />
                        </div>
                    )}
                </section>

                <section className="flex flex-col gap-2">
                    <h3 className={HEADING}>Description</h3>
                    <PostingOpening description={posting.description} />
                    <Link
                        {...actionProps("posting_full_page_clicked", {
                            source: "JobDrawer",
                            posting_id: posting.posting_id,
                        })}
                        to={postingDetailPath(posting.posting_id)}
                        className="text-[14px] leading-5 font-medium text-brand-ink"
                    >
                        Read the whole posting
                    </Link>
                </section>

                <section className="flex flex-col gap-2">
                    <h3 className={HEADING}>Record</h3>
                    <RecordList entries={record} compact />
                </section>
            </div>
        </aside>
    );
}
